/**
 * ============================================================================
 * EquipmentDetail — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /equipment/:id
 * Purpose: full equipment dossier — header (tag + name + criticality + GAMP +
 *          lifecycle status + last-calibrated indicator), seven tabs
 *          (Timeline / Qualifications / Calibrations / PM Records /
 *          Requalifications / Cross-Refs / Specs), and an actions sidebar
 *          with Transition / Record Calibration / Record PM / Update Location
 *          / Retire (the last via the dual-sig RetireDialog).
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Editorial layout, generous whitespace.
 *   - Tabs are real <button role="tab"> with arrow-key navigation.
 * ============================================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './EquipmentDetail.module.css';
import { useEquipmentDetailQuery } from './useEquipmentDetailQuery';
import { EquipmentTimeline } from './EquipmentTimeline';
import { QualificationChain } from './QualificationChain';
import { CalibrationsTable } from './CalibrationsTable';
import { PMRecordsTable } from './PMRecordsTable';
import { RequalificationsTable } from './RequalificationsTable';
import { CrossRefsList } from './CrossRefsList';
import { RetireDialog } from './RetireDialog';
import type {
  CalibrationOutcome,
  EquipmentSpecs as EquipmentSpecsT,
} from './useEquipmentDetailQuery';
import type {
  EquipmentCriticality,
  EquipmentLifecycleStatus,
  GampClass,
} from '../EquipmentWorkbench/useEquipmentQuery';

type Tab =
  | 'timeline'
  | 'qualifications'
  | 'calibrations'
  | 'pm'
  | 'requalifications'
  | 'crossRefs'
  | 'specs';

const TABS: Tab[] = [
  'timeline',
  'qualifications',
  'calibrations',
  'pm',
  'requalifications',
  'crossRefs',
  'specs',
];

type DialogState =
  | { kind: 'closed' }
  | { kind: 'retire' };

export interface EquipmentDetailProps {
  equipmentId?: string;
}

export function EquipmentDetail({
  equipmentId = 'eq-demo',
}: EquipmentDetailProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const detailQ = useEquipmentDetailQuery(equipmentId);
  const data = detailQ.data;

  const [tab, setTab] = useState<Tab>('timeline');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  function onTabKey(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (idx + 1) % TABS.length;
      tabRefs.current[next]?.focus();
      setTab(TABS[next]!);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (idx - 1 + TABS.length) % TABS.length;
      tabRefs.current[prev]?.focus();
      setTab(TABS[prev]!);
    } else if (e.key === 'Home') {
      e.preventDefault();
      tabRefs.current[0]?.focus();
      setTab(TABS[0]!);
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = TABS.length - 1;
      tabRefs.current[last]?.focus();
      setTab(TABS[last]!);
    }
  }

  useEffect(() => {
    /* no-op: kept for parity with the other tabbed screens */
  }, [tab]);

  return (
    <div className={wstyles.adminScreen}>
      {detailQ.isLoading && (
        <Card>
          <CardBody>
            <div role="status" aria-live="polite">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={styles.skeletonRow} aria-hidden="true" />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {detailQ.isError && (
        <Card>
          <CardBody>
            <div role="alert" className={styles.errorBanner}>
              <span>
                {t('equipment.detail.errors.loadFailed', {
                  defaultValue: 'Could not load equipment dossier.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => detailQ.refetch()}
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {!detailQ.isLoading && !detailQ.isError && data && (
        <>
          <EquipmentHeader
            tag={data.equipment_tag}
            name={data.name}
            criticality={data.criticality}
            gampClass={data.gamp_class}
            lifecycleStatus={data.lifecycle_status}
            lastCalAt={data.last_calibration_at_utc}
            lastCalOutcome={data.last_calibration_outcome}
            dateFmt={dateFmt}
          />

          <div className={styles.layout}>
            <main className={styles.mainCol}>
              <Card>
                <CardHeader>
                  <div
                    role="tablist"
                    aria-label={t('equipment.detail.tabs.listAria', {
                      defaultValue: 'Equipment dossier sections',
                    })}
                    className={styles.tablist}
                  >
                    {TABS.map((tk, idx) => {
                      const active = tk === tab;
                      return (
                        <button
                          key={tk}
                          ref={(el) => {
                            tabRefs.current[idx] = el;
                          }}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          aria-controls={`eq-tabpanel-${tk}`}
                          id={`eq-tab-${tk}`}
                          tabIndex={active ? 0 : -1}
                          className={`${styles.tabButton} ${active ? styles.tabButtonActive : ''}`}
                          onClick={() => setTab(tk)}
                          onKeyDown={(e) => onTabKey(e, idx)}
                        >
                          {t(`equipment.detail.tabs.${tk}`, {
                            defaultValue: tk,
                          })}
                        </button>
                      );
                    })}
                  </div>
                </CardHeader>
                <CardBody>
                  <div
                    role="tabpanel"
                    id={`eq-tabpanel-${tab}`}
                    aria-labelledby={`eq-tab-${tab}`}
                  >
                    {tab === 'timeline' && (
                      <EquipmentTimeline events={data.timeline} />
                    )}
                    {tab === 'qualifications' && (
                      <QualificationChain steps={data.qualification_chain} />
                    )}
                    {tab === 'calibrations' && (
                      <CalibrationsTable rows={data.calibrations} />
                    )}
                    {tab === 'pm' && <PMRecordsTable rows={data.pm_records} />}
                    {tab === 'requalifications' && (
                      <RequalificationsTable rows={data.requalifications} />
                    )}
                    {tab === 'crossRefs' && (
                      <CrossRefsList rows={data.cross_refs} />
                    )}
                    {tab === 'specs' && <EquipmentSpecs specs={data.specs} />}
                  </div>
                </CardBody>
              </Card>
            </main>

            <aside
              className={styles.sideCol}
              aria-label={t('equipment.detail.actions.aside', {
                defaultValue: 'Equipment actions',
              })}
            >
              <Card>
                <CardHeader>
                  <h2 className={wstyles.sectionTitle}>
                    {t('equipment.detail.actions.title', {
                      defaultValue: 'Actions',
                    })}
                  </h2>
                </CardHeader>
                <CardBody>
                  <div className={styles.actionsList}>
                    <Button
                      variant="primary"
                      onClick={() =>
                        window.alert(
                          t('equipment.detail.actions.transitionStub', {
                            defaultValue:
                              'Transition lifecycle: follow-on step — see the transition flow.',
                          }),
                        )
                      }
                      aria-label={t('equipment.detail.actions.transitionAria', {
                        defaultValue: 'Transition equipment lifecycle status',
                      })}
                    >
                      {t('equipment.detail.actions.transition', {
                        defaultValue: 'Transition lifecycle',
                      })}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        navigate({ to: '/equipment/calibrations' });
                      }}
                      aria-label={t(
                        'equipment.detail.actions.recordCalibrationAria',
                        {
                          defaultValue:
                            'Open the calibration tray to record a new calibration',
                        },
                      )}
                    >
                      {t('equipment.detail.actions.recordCalibration', {
                        defaultValue: 'Record calibration',
                      })}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        navigate({ to: '/equipment/pm' });
                      }}
                      aria-label={t('equipment.detail.actions.recordPmAria', {
                        defaultValue:
                          'Open the PM tray to record a new PM',
                      })}
                    >
                      {t('equipment.detail.actions.recordPm', {
                        defaultValue: 'Record PM',
                      })}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        window.alert(
                          t('equipment.detail.actions.updateLocationStub', {
                            defaultValue:
                              'Update location: opens a follow-on dialog that schedules a LOCATION_CHANGE requalification.',
                          }),
                        )
                      }
                    >
                      {t('equipment.detail.actions.updateLocation', {
                        defaultValue: 'Update location',
                      })}
                    </Button>
                    <Button
                      variant="tertiary"
                      onClick={() => setDialog({ kind: 'retire' })}
                      aria-label={t('equipment.detail.actions.retireAria', {
                        defaultValue:
                          'Retire equipment — dual signature and 200-char rationale required',
                      })}
                    >
                      {t('equipment.detail.actions.retire', {
                        defaultValue: 'Retire',
                      })}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </aside>
          </div>

          {dialog.kind === 'retire' && (
            <RetireDialog
              equipmentId={data.equipment_id}
              equipmentName={data.name}
              equipmentTag={data.equipment_tag}
              onClose={() => setDialog({ kind: 'closed' })}
              onRetired={() => {
                setDialog({ kind: 'closed' });
                detailQ.refetch();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Header — tag + name + criticality + GAMP + lifecycle + last-calibrated.
 * --------------------------------------------------------------------------- */

interface EquipmentHeaderProps {
  tag: string;
  name: string;
  criticality: EquipmentCriticality;
  gampClass: GampClass;
  lifecycleStatus: EquipmentLifecycleStatus;
  lastCalAt: string | null;
  lastCalOutcome: CalibrationOutcome | null;
  dateFmt: Intl.DateTimeFormat;
}

function EquipmentHeader({
  tag,
  name,
  criticality,
  gampClass,
  lifecycleStatus,
  lastCalAt,
  lastCalOutcome,
  dateFmt,
}: EquipmentHeaderProps) {
  const { t } = useTranslation();

  const critClass: Record<EquipmentCriticality, string> = {
    CRITICAL: styles.critCritical,
    MAJOR: styles.critMajor,
    MINOR: styles.critMinor,
    NON_GMP: styles.critNonGmp,
  };
  const gampClassName: Record<GampClass, string> = {
    GAMP5: styles.gamp5,
    GAMP4: styles.gamp4,
    GAMP3: styles.gamp3,
    GAMP1: styles.gamp1,
  };
  const lifeClass: Record<EquipmentLifecycleStatus, string> = {
    PLANNED: styles.lifeEarly,
    URS_APPROVED: styles.lifeEarly,
    DQ_APPROVED: styles.lifeEarly,
    FAT_PASSED: styles.lifeEarly,
    SAT_PASSED: styles.lifeEarly,
    IQ_PASSED: styles.lifeEarly,
    OQ_PASSED: styles.lifeEarly,
    PQ_PASSED: styles.lifeEarly,
    QUALIFIED: styles.lifeQualified,
    RESTRICTED: styles.lifeRestricted,
    RETIRED: styles.lifeRetired,
    DECOMMISSIONED: styles.lifeDecommissioned,
  };

  const calOutcomeClass: Record<CalibrationOutcome, string> = {
    PASSED: styles.lastCalPassed,
    FAILED: styles.lastCalFailed,
    OUT_OF_TOLERANCE: styles.lastCalOot,
  };

  return (
    <header className={styles.detailHeader}>
      <div className={styles.detailHeaderMain}>
        <div className={styles.detailTagRow}>
          <code className={styles.detailTag}>{tag}</code>
        </div>
        <h1 className={styles.detailName}>{name}</h1>
        <div className={styles.detailChips}>
          <span className={`${styles.chip} ${critClass[criticality]}`}>
            {t(`equipment.criticality.${criticality}`, {
              defaultValue: criticality,
            })}
          </span>
          <span className={`${styles.chip} ${gampClassName[gampClass]}`}>
            {t(`equipment.gamp.${gampClass}`, { defaultValue: gampClass })}
          </span>
          <span className={`${styles.chip} ${lifeClass[lifecycleStatus]}`}>
            {t(`equipment.lifecycle.${lifecycleStatus}`, {
              defaultValue: lifecycleStatus,
            })}
          </span>
        </div>
        {lastCalAt && (
          <p
            className={`${styles.lastCalRow} ${
              lastCalOutcome ? calOutcomeClass[lastCalOutcome] : ''
            }`}
          >
            {t('equipment.detail.header.lastCal', {
              defaultValue: 'Last calibrated: {{ts}} ({{outcome}})',
              ts: dateFmt.format(new Date(lastCalAt)),
              outcome: lastCalOutcome
                ? t(`equipment.detail.calibrations.outcome.${lastCalOutcome}`, {
                    defaultValue: lastCalOutcome,
                  })
                : t('equipment.detail.header.lastCalUnknown', {
                    defaultValue: 'unknown',
                  }),
            })}
          </p>
        )}
        {!lastCalAt && (
          <p className={styles.lastCalRow}>
            {t('equipment.detail.header.noLastCal', {
              defaultValue: 'No calibration recorded yet.',
            })}
          </p>
        )}
      </div>
    </header>
  );
}

/* ----------------------------------------------------------------------------
 * EquipmentSpecs — labelled-grid view of manufacturer / model / serial /
 *  cadences / location / cadence overrides.
 * --------------------------------------------------------------------------- */

function EquipmentSpecs({ specs }: { specs: EquipmentSpecsT }) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const fields: Array<{ key: string; defaultLabel: string; value: React.ReactNode }> = [
    {
      key: 'manufacturer',
      defaultLabel: 'Manufacturer',
      value: specs.manufacturer ?? '—',
    },
    {
      key: 'model',
      defaultLabel: 'Model',
      value: specs.model ?? '—',
    },
    {
      key: 'serial',
      defaultLabel: 'Serial',
      value: specs.serial ?? '—',
    },
    {
      key: 'categoryKey',
      defaultLabel: 'Category',
      value: specs.category_key ?? '—',
    },
    {
      key: 'location',
      defaultLabel: 'Location',
      value: specs.location ?? '—',
    },
    {
      key: 'site',
      defaultLabel: 'Site',
      value: specs.site_code ?? '—',
    },
    {
      key: 'calCadence',
      defaultLabel: 'Calibration cadence',
      value: t('equipment.detail.specs.daysValue', {
        defaultValue: '{{n}} days',
        n: nf.format(specs.calibration_cadence_days),
      }),
    },
    {
      key: 'pmCadence',
      defaultLabel: 'PM cadence',
      value: t('equipment.detail.specs.daysValue', {
        defaultValue: '{{n}} days',
        n: nf.format(specs.pm_cadence_days),
      }),
    },
    {
      key: 'requalCadence',
      defaultLabel: 'Requalification cadence',
      value: t('equipment.detail.specs.daysValue', {
        defaultValue: '{{n}} days',
        n: nf.format(specs.requalification_cadence_days),
      }),
    },
  ];

  return (
    <div>
      <dl className={styles.specsGrid}>
        {fields.map((f) => (
          <div key={f.key} style={{ display: 'contents' }}>
            <dt className={styles.specsLabel}>
              {t(`equipment.detail.specs.${f.key}`, {
                defaultValue: f.defaultLabel,
              })}
            </dt>
            <dd className={styles.specsValue}>{f.value}</dd>
          </div>
        ))}
      </dl>
      {Object.keys(specs.cadence_overrides).length > 0 && (
        <details style={{ marginBlockStart: 'var(--space-4)' }}>
          <summary>
            {t('equipment.detail.specs.overridesTitle', {
              defaultValue: 'Cadence overrides',
            })}
          </summary>
          <pre className={styles.payloadPre}>
            {JSON.stringify(specs.cadence_overrides, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
